import { z } from "zod";
/**
 * Market modülü sabitleri (Prompt 16, ADR 0016).
 *
 * STOK İZLENEN ürün türleri TEK yerde tanımlıdır: kasa satışı tüketicisi, açılış stoğu, transfer, fire ve sayım
 * yalnızca bu türlerin stoğunu tutar. Akaryakıt / LPG stoğu akaryakıt modülündedir; hizmetlerin stoğu yoktur.
 */
export const STOCK_TRACKED_PRODUCT_TYPES = [
    "MARKET_ITEM",
    "LUBRICANT",
    "CAFE_ITEM",
    // Prompt 18: yıkama kimyasalı — istasyonun `WASH` deposunda, tüketimle (`CONSUMPTION`) düşer.
    "CONSUMABLE",
];
export function isStockTrackedProductType(type) {
    return STOCK_TRACKED_PRODUCT_TYPES.includes(type);
}
export const STOCK_MOVEMENT_TYPES = [
    "OPENING",
    "RECEIPT",
    "RECEIPT_REVERSAL",
    "SALE",
    "SALE_RETURN",
    "TRANSFER_OUT",
    "TRANSFER_IN",
    "WASTE",
    "COUNT_ADJUSTMENT",
    "COST_ADJUSTMENT",
    "CONSUMPTION",
];
export const stockMovementTypeSchema = z.enum(STOCK_MOVEMENT_TYPES);
export const STOCK_MOVEMENT_TYPE_LABELS = {
    OPENING: "Açılış",
    RECEIPT: "Mal kabul",
    RECEIPT_REVERSAL: "Mal kabul iptali",
    SALE: "Kasa satışı",
    SALE_RETURN: "Kasa iadesi / iptali",
    TRANSFER_OUT: "Transfer çıkışı",
    TRANSFER_IN: "Transfer girişi",
    WASTE: "Fire / imha",
    COUNT_ADJUSTMENT: "Sayım farkı",
    COST_ADJUSTMENT: "Maliyet düzeltmesi",
    CONSUMPTION: "Yıkama tüketimi",
};
export const WAREHOUSE_KINDS = ["STORE", "BACKROOM", "WASH"];
export const warehouseKindSchema = z.enum(WAREHOUSE_KINDS);
export const WAREHOUSE_KIND_LABELS = {
    STORE: "Mağaza / reyon",
    BACKROOM: "Depo",
    WASH: "Yıkama kimyasal deposu",
};
/** Muhasebe fişinin durumu (belge başına). */
export const MARKET_LEDGER_STATUSES = ["NONE", "POSTED", "QUEUED"];
export const marketLedgerStatusSchema = z.enum(MARKET_LEDGER_STATUSES);
export const STOCK_TRANSFER_STATUSES = ["DRAFT", "SENT", "RECEIVED", "CANCELLED"];
export const stockTransferStatusSchema = z.enum(STOCK_TRANSFER_STATUSES);
export const STOCK_TRANSFER_STATUS_LABELS = {
    DRAFT: "Taslak",
    SENT: "Yolda",
    RECEIVED: "Teslim alındı",
    CANCELLED: "İptal edildi",
};
export const STOCK_WRITE_OFF_STATUSES = [
    "DRAFT",
    "PENDING_APPROVAL",
    "POSTED",
    "REJECTED",
    "CANCELLED",
];
export const stockWriteOffStatusSchema = z.enum(STOCK_WRITE_OFF_STATUSES);
export const STOCK_WRITE_OFF_STATUS_LABELS = {
    DRAFT: "Taslak",
    PENDING_APPROVAL: "Onay bekliyor",
    POSTED: "Kaydedildi",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
export const WRITE_OFF_REASONS = ["EXPIRED", "DAMAGED", "THEFT", "INTERNAL_USE", "OTHER"];
export const writeOffReasonSchema = z.enum(WRITE_OFF_REASONS);
export const WRITE_OFF_REASON_LABELS = {
    EXPIRED: "Son kullanma tarihi geçti",
    DAMAGED: "Hasarlı / bozuk",
    THEFT: "Hırsızlık / kayıp",
    INTERNAL_USE: "İç kullanım",
    OTHER: "Diğer",
};
/** Fire nedeni → kayıp anahtarı (market ayarı `writeOffLossKeys`). */
export const WRITE_OFF_LOSS_KEYS = ["NORMAL_LOSS", "ABNORMAL_LOSS"];
export const writeOffLossKeySchema = z.enum(WRITE_OFF_LOSS_KEYS);
export const STOCK_COUNT_KINDS = ["FULL", "PARTIAL", "CYCLE"];
export const stockCountKindSchema = z.enum(STOCK_COUNT_KINDS);
export const STOCK_COUNT_KIND_LABELS = {
    FULL: "Tam sayım",
    PARTIAL: "Kısmi sayım",
    CYCLE: "Döngüsel sayım",
};
export const STOCK_COUNT_STATUSES = ["COUNTING", "SUBMITTED", "APPROVED", "CANCELLED"];
export const stockCountStatusSchema = z.enum(STOCK_COUNT_STATUSES);
export const STOCK_COUNT_STATUS_LABELS = {
    COUNTING: "Sayılıyor",
    SUBMITTED: "Onaya gönderildi",
    APPROVED: "Onaylandı",
    CANCELLED: "İptal edildi",
};
export const STOCK_COUNT_SCAN_KINDS = ["SCAN", "MANUAL", "CORRECTION"];
export const stockCountScanKindSchema = z.enum(STOCK_COUNT_SCAN_KINDS);
export const EXPIRY_STATES = ["EXPIRED", "EXPIRING", "OK"];
export const expiryStateSchema = z.enum(EXPIRY_STATES);
export const EXPIRY_STATE_LABELS = {
    EXPIRED: "SKT geçti",
    EXPIRING: "SKT yaklaşıyor",
    OK: "Sorunsuz",
};
// ── Ortak alan şemaları ──────────────────────────────────────────────────────
/** Birim maliyet (₺, en fazla 6 ondalık, ≥ 0): hareketli ortalama gösterimi. */
export const marketUnitCostSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, "Birim maliyet en fazla 6 ondalıklı, sıfır ya da pozitif sayı olmalıdır")
    .describe('Birim maliyet (₺, dizge, en fazla 6 ondalık). Örn. "12.345678"');
/** Pozitif miktar girdisi (≤ 3 ondalık). */
export const positiveQuantitySchema = z
    .string()
    .regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar en fazla 3 ondalıklı pozitif sayı olmalıdır")
    .refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır")
    .describe('Miktar (dizge, en fazla 3 ondalık, > 0). Örn. "12.500"');
/** Parti numarası (tedarikçinin lot numarası). */
export const lotNoSchema = z.string().trim().min(1).max(60).describe("Parti (lot) numarası");
// ── Satın alma (aşama B) ─────────────────────────────────────────────────────
/** Tedarikçi siparişi durumları (geçiş tablosu: `apps/api/src/market/domain/states.ts`). */
export const PURCHASE_ORDER_STATUSES = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "SENT",
    "PARTIALLY_RECEIVED",
    "RECEIVED",
    "CLOSED",
    "REJECTED",
    "CANCELLED",
];
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);
export const PURCHASE_ORDER_STATUS_LABELS = {
    DRAFT: "Taslak",
    PENDING_APPROVAL: "Onay bekliyor",
    APPROVED: "Onaylandı",
    SENT: "Tedarikçiye gönderildi",
    PARTIALLY_RECEIVED: "Kısmen teslim alındı",
    RECEIVED: "Teslim alındı",
    CLOSED: "Kapatıldı",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
export const GOODS_RECEIPT_STATUSES = ["DRAFT", "POSTED", "CANCELLED"];
export const goodsReceiptStatusSchema = z.enum(GOODS_RECEIPT_STATUSES);
export const GOODS_RECEIPT_STATUS_LABELS = {
    DRAFT: "Taslak",
    POSTED: "Kaydedildi",
    CANCELLED: "İptal edildi",
};
/** Mal kabul maliyeti: kabulde GEÇİCİ (sipariş / liste fiyatı), faturayla eşleşince KESİN (ADR 0016 §10). */
export const RECEIPT_COST_STATUSES = ["PROVISIONAL", "FINAL"];
export const receiptCostStatusSchema = z.enum(RECEIPT_COST_STATUSES);
export const RECEIPT_COST_STATUS_LABELS = {
    PROVISIONAL: "Geçici maliyet",
    FINAL: "Kesin maliyet",
};
export const INVOICE_MATCH_STATUSES = ["MATCHED", "PENDING_APPROVAL", "APPROVED", "UNMATCHED"];
export const invoiceMatchStatusSchema = z.enum(INVOICE_MATCH_STATUSES);
export const INVOICE_MATCH_STATUS_LABELS = {
    MATCHED: "Eşleşti",
    PENDING_APPROVAL: "Fark onayı bekliyor",
    APPROVED: "Fark onaylandı",
    UNMATCHED: "Eşleşme geri alındı",
};
export const INVOICE_MATCH_RESULTS = ["MATCHED", "MATCHED_WITH_DIFFERENCES"];
export const invoiceMatchResultSchema = z.enum(INVOICE_MATCH_RESULTS);
export const INVOICE_MATCH_RESULT_LABELS = {
    MATCHED: "Tam eşleşme",
    MATCHED_WITH_DIFFERENCES: "Farklı eşleşme",
};
export const MATCH_LINE_STATUSES = [
    "OK",
    "QUANTITY_DIFFERENCE",
    "PRICE_DIFFERENCE",
    "QUANTITY_AND_PRICE_DIFFERENCE",
    "NOT_RECEIVED",
    "NOT_INVOICED",
    "NOT_ORDERED",
];
export const matchLineStatusSchema = z.enum(MATCH_LINE_STATUSES);
export const MATCH_LINE_STATUS_LABELS = {
    OK: "Uyumlu",
    QUANTITY_DIFFERENCE: "Miktar farkı",
    PRICE_DIFFERENCE: "Fiyat farkı",
    QUANTITY_AND_PRICE_DIFFERENCE: "Miktar ve fiyat farkı",
    NOT_RECEIVED: "Faturada var, kabul edilmedi",
    NOT_INVOICED: "Kabul edildi, faturada yok",
    NOT_ORDERED: "Sipariş / kabul dışı fatura satırı",
};
export const ORDER_SUGGESTION_REASONS = ["BELOW_MIN_AT_ARRIVAL", "NO_LIMITS", "SUFFICIENT"];
export const orderSuggestionReasonSchema = z.enum(ORDER_SUGGESTION_REASONS);
// ── Lisanslar ────────────────────────────────────────────────────────────────
export const STATION_LICENSE_TYPES = ["TOBACCO_SALES", "ALCOHOL_SALES", "EV_CHARGING", "OTHER"];
export const stationLicenseTypeSchema = z.enum(STATION_LICENSE_TYPES);
export const STATION_LICENSE_TYPE_LABELS = {
    TOBACCO_SALES: "Tütün mamulü satış belgesi",
    ALCOHOL_SALES: "Alkollü içki satış belgesi",
    // Prompt 19 (ADR 0019 §12): şarj hizmeti lisansı — bitiş uyarısı aynı akışla.
    EV_CHARGING: "Şarj hizmeti lisansı",
    OTHER: "Diğer belge",
};
/** Belgenin bugünkü durumu (uyarı eşiği market ayarı `licenseWarningDays`). */
export const STATION_LICENSE_STATES = ["VALID", "EXPIRING", "EXPIRED", "NOT_YET_VALID", "INACTIVE"];
export const stationLicenseStateSchema = z.enum(STATION_LICENSE_STATES);
export const STATION_LICENSE_STATE_LABELS = {
    VALID: "Geçerli",
    EXPIRING: "Süresi dolmak üzere",
    EXPIRED: "Süresi doldu",
    NOT_YET_VALID: "Henüz yürürlükte değil",
    INACTIVE: "Pasif",
};
// ── Raporlar ─────────────────────────────────────────────────────────────────
export const MARKET_REPORT_GROUPINGS = ["STATION", "CATEGORY", "PRODUCT"];
export const marketReportGroupingSchema = z.enum(MARKET_REPORT_GROUPINGS);
/** Satış fiyatı (KDV dâhil ₺, en fazla 4 ondalık, > 0). */
export const marketSalePriceSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,4})?$/, "Fiyat en fazla 4 ondalıklı pozitif sayı olmalıdır")
    .refine((value) => Number(value) > 0, "Fiyat sıfırdan büyük olmalıdır")
    .describe('Satış fiyatı, KDV dâhil (₺, dizge). Örn. "15.25"');
/** Alış birim fiyatı / geçici maliyet (KDV hariç ₺, en fazla 4 ondalık, ≥ 0). */
export const purchaseUnitPriceSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,4})?$/, "Birim fiyat en fazla 4 ondalıklı, sıfır ya da pozitif sayı olmalıdır")
    .describe('Alış birim fiyatı, KDV HARİÇ (₺, dizge). Örn. "12.5000"');
/** Sıfır ya da pozitif miktar girdisi (≤ 3 ondalık). */
export const nonNegativeQuantitySchema = z
    .string()
    .regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar en fazla 3 ondalıklı, sıfır ya da pozitif sayı olmalıdır")
    .describe('Miktar (dizge, en fazla 3 ondalık, ≥ 0). Örn. "0"');
//# sourceMappingURL=enums.js.map